import type { ApiClient } from '../client';

export interface JustPulledBadge {
  id: number | null;
  name: string;
}

/** Pack the pulled card came from — feeds the bottom row in JustPulledCard
 *  (Figma 226:374). Null until backend exposes `pool_offer` on the item. */
export interface JustPulledOffer {
  id: number;
  title: string;
  imageUrl: string | null;
}

export interface JustPulledItem {
  id: number;
  title: string;
  imageUrl: string | null;
  openedAt: string | null;
  badge: JustPulledBadge | null;
  offer: JustPulledOffer | null;
}

export interface JustPulledPage {
  items: JustPulledItem[];
  totalCount: number;
  page: number;
  per: number;
}

export interface JustPulledParams {
  page?: number;
  per?: number;
  /** Limit the feed to pulls from a single pack (pool_offer.id). Omit
   *  for the global "all packs" feed. */
  poolOfferId?: number | string | null;
  signal?: AbortSignal;
}

interface RawJustPulledItem {
  id?: number | string;
  title?: string | null;
  image_url?: string | null;
  opened_at?: string | null;
  badge?: { id?: number | null; name?: string | null } | null;
  pool_offer?: {
    id?: number | string;
    title?: string | null;
    image_url?: string | null;
  } | null;
}

interface RawMeta {
  total_count?: number;
  page?: number;
  per?: number;
}

/**
 * Recent pulls across all users / all pool offers — feeds the live "Just
 * Pulled" strip on the Pack page and the full list view at
 * `?view=just-pulled`. Paginated. Sorted by opened_at DESC server-side.
 */
export async function getJustPulled(
  client: ApiClient,
  params: JustPulledParams = {},
): Promise<JustPulledPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per) qs.set('per', String(params.per));
  if (params.poolOfferId !== undefined && params.poolOfferId !== null && params.poolOfferId !== '') {
    qs.set('pool_offer_id', String(params.poolOfferId));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  const raw = await client.request<{
    pool_items: RawJustPulledItem[];
    meta?: RawMeta;
  }>(`/loot_machine/api/game_platform/iframe/v1/pool_items/just_pulled${suffix}`, {
    method: 'GET',
    ...(params.signal ? { signal: params.signal } : {}),
  });

  const items = (raw.pool_items ?? []).map<JustPulledItem>((i) => ({
    id: Number(i.id ?? 0),
    title: (i.title ?? '').toString(),
    imageUrl: typeof i.image_url === 'string' && i.image_url ? i.image_url : null,
    openedAt: typeof i.opened_at === 'string' ? i.opened_at : null,
    badge: i.badge
      ? {
          id: typeof i.badge.id === 'number' ? i.badge.id : null,
          name: (i.badge.name ?? '').toString(),
        }
      : null,
    offer:
      i.pool_offer && (typeof i.pool_offer.id === 'number' || typeof i.pool_offer.id === 'string')
        ? {
            id: Number(i.pool_offer.id),
            title: (i.pool_offer.title ?? '').toString(),
            imageUrl:
              typeof i.pool_offer.image_url === 'string' && i.pool_offer.image_url
                ? i.pool_offer.image_url
                : null,
          }
        : null,
  }));

  return {
    items,
    totalCount: Number(raw.meta?.total_count ?? items.length),
    page: Number(raw.meta?.page ?? 1),
    per: Number(raw.meta?.per ?? items.length),
  };
}
